import React, { useEffect, useState, useContext } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Link } from "react-router-dom";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Button,
  Grid,
  Typography,
  IconButton,
  Alert,
  AlertTitle,
  Tooltip,
  DialogContent,
  DialogActions,
  Dialog,
  Paper,
  Box,
  ListItem,
  List,
  ListItemButton,
  CircularProgress,
  Divider,
  Skeleton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SplitscreenIcon from "@mui/icons-material/Splitscreen";

import { useTranslation } from "react-i18next";
import { UserContext } from "../App";
import { deleteData, fetchData } from "../utils/api";
import { makeStringReadable } from "../utils/table-utils";
import { reactMUILocale } from "../i18n";

export default function Overview() {
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    datasets,
    fetchDatasets,
    setCurrentDatasetIdx,
    setCurrentExperimentIdx,
  } = useContext(UserContext);

  const [experimentData, setExperimentData] = useState([]);
  const [columnInfo, setColumnInfo] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedExperiments, setSelectedExperiments] = useState([]);
  const [deleteType, setDeleteType] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const [datasetDeleteLoading, setDatasetDeleteLoading] = useState(null); // index
  const [experimentDeleteLoading, setExperimentDeleteLoading] = useState(null); // experimentName
  const [splitFileDeleteLoading, setSplitFileDeleteLoading] = useState(null); // fileName

  const [loadingDatasets, setLoadingDatasets] = useState(true);
  const [loadingExperiments, setLoadingExperiments] = useState(true);
  const [datasetToDeleteIdx, setDatasetToDeleteIdx] = useState(null);
  const [splitFileToDelete, setSplitFileToDelete] = useState(null);
  const [splitFiles, setSplitFiles] = useState([]);
  const [loadingSplitFiles, setLoadingSplitFiles] = useState(true);

  const fetchExperiments = async (datasetName) => {
    setLoadingExperiments(true);
    const dataset = await fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiments`
    );
    const experimentsList = [];

    if (dataset.experiments.length !== 0) {
      dataset.experiments.forEach((experiment) => {
        const updatedExperiment = {
          ...experiment,
          is_created: experiment.experimentProgress.created,
          is_processed: experiment.experimentProgress.processed,
          num_trained_models: experiment.experimentProgress.trained,
        };
        delete updatedExperiment.experimentProgress;
        experimentsList.push(updatedExperiment);
      });

      setExperimentData(experimentsList);
      const columnNames = Object.keys(experimentsList.at(0));
      const columnInfoList = columnNames.map((name) => ({
        headerName: makeStringReadable(name),
        field: name,
        width: 150,
      }));
      setColumnInfo(columnInfoList);
    } else {
      setExperimentData([]);
    }
    setLoadingExperiments(false);
  };

  const fetchSplitFiles = async (datasetName) => {
    setLoadingSplitFiles(true);
    try {
      const response = await fetchData(
        `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/split-info`
      );
      setSplitFiles(response.splitFiles || []);
    } catch (e) {
      setSplitFiles([]);
    }
    setLoadingSplitFiles(false);
  };

  const handleListItemClick = (event, index) => {
    setSelectedIndex(index);
    fetchExperiments(datasets[index].datasetName);
    fetchSplitFiles(datasets[index].datasetName);
  };

  const handleDialogDisplay = () => setOpen(!open);

  const handleDeleteType = (type, index = null, fileName = null) => {
    if (type === "Dataset") setDatasetToDeleteIdx(index);
    if (type === "SplitFile" || type === "Experiment")
      setSplitFileToDelete(fileName);
    handleDialogDisplay();
    setDeleteType(type);
  };

  const handleDatasetDelete = async (datasetName, index) => {
    setDatasetDeleteLoading(index);
    try {
      const data = await deleteData(
        `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}`
      );
      if (data.status === 200) {
        await fetchDatasets({});
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else {
        setError(true);
      }
    } catch (e) {
      setError(true);
    } finally {
      setDatasetDeleteLoading(null);
    }
  };

  const handleExperimentsDelete = async (datasetName, experimentNames) => {
    setExperimentDeleteLoading(experimentNames[0]);
    try {
      const deletePromises = experimentNames.map((experimentName) =>
        deleteData(
          `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experimentName}`
        )
      );
      const responses = await Promise.all(deletePromises);
      const allSuccessful = responses.every((res) => res.status === 200);
      if (allSuccessful) fetchExperiments(datasetName);
      else setError(true);
    } catch (e) {
      setError(true);
    } finally {
      setExperimentDeleteLoading(null);
    }
  };

  const handleSplitFileDelete = async (datasetName, fileName) => {
    setSplitFileDeleteLoading(fileName);
    try {
      const data = await deleteData(
        `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/split-data/${fileName}`
      );
      if (data.status === 200) fetchSplitFiles(datasetName);
      else setError(true);
    } catch (e) {
      setError(true);
    } finally {
      setSplitFileDeleteLoading(null);
    }
  };
  const handleDelete = () => {
    handleDialogDisplay();

    if (deleteType === "Experiment") {
      const experimentNames =
        typeof splitFileToDelete === "string"
          ? [splitFileToDelete] // single experiment
          : selectedExperiments.map(
              (idx) => experimentData[idx].experimentName
            );

      handleExperimentsDelete(
        datasets[selectedIndex].datasetName,
        experimentNames
      );
    } else if (deleteType === "Dataset") {
      handleDatasetDelete(datasets[datasetToDeleteIdx].datasetName);
    } else if (deleteType === "SplitFile") {
      handleSplitFileDelete(
        datasets[selectedIndex].datasetName,
        splitFileToDelete
      );
    }
  };

  const handleButtonClick = () => {
    handleDelete();
  };

  useEffect(() => {
    const loadData = async () => {
      setLoadingDatasets(true);
      await fetchDatasets({});
      setLoadingDatasets(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (datasets.length >= 1) {
      fetchExperiments(datasets[selectedIndex].datasetName);
      fetchSplitFiles(datasets[selectedIndex].datasetName);
    } else {
      setExperimentData([]);
      setSplitFiles([]);
    }
  }, [datasets]);

  useEffect(() => {
    setCurrentDatasetIdx(selectedIndex);
    setCurrentExperimentIdx(0);
  }, [selectedIndex]);

  return (
    <Grid
      id="overview-container"
      container
      columnSpacing={2}
      rowSpacing={2}
      sx={{ padding: theme.spacing(4, 2) }}
    >
      {/* ------------------------Datasets Panel------------------------------ */}
      <Grid item xs={12} lg={3}>
        <Paper elevation={3} sx={{ p: 2, height: "fit-content" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">
              {t("overview.MyDatasetsTitle")}
            </Typography>
            <Link to="/new-dataset" id="add-dataset-topbar-link">
              <Tooltip title={t("overview.DataButton")}>
                <IconButton aria-label={t("overview.DataButton")}>
                  <PostAddIcon />
                </IconButton>
              </Tooltip>
            </Link>
          </Box>
          <List sx={{ maxHeight: 600, overflow: "auto" }}>
            {loadingDatasets
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton
                    variant="rectangular"
                    height={50}
                    key={`skeleton-ds-${i}`}
                    sx={{ mb: 1, borderRadius: 1 }}
                  />
                ))
              : datasets.map((ds, index) => (
                  <React.Fragment key={`ds-${ds.datasetName}`}>
                    <ListItem selected={selectedIndex === index} disablePadding>
                      <ListItemButton
                        onClick={(e) => handleListItemClick(e, index)}
                      >
                        <Grid
                          container
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Grid item sx={{ maxWidth: "80%" }}>
                            <Typography noWrap>
                              {index + 1}. {ds.datasetName}
                            </Typography>
                          </Grid>
                          <Grid item>
                            {datasetDeleteLoading === index ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Tooltip
                                title={t("overview.DeleteDatasetButton")}
                              >
                                <IconButton
                                  sx={{
                                    color: theme.palette.error.main,
                                    "&:hover": {
                                      bgcolor: theme.palette.error.light,
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteType("Dataset", index);
                                    handleDeleteType("Dataset", index);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Grid>
                        </Grid>
                      </ListItemButton>
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                ))}
          </List>
        </Paper>
      </Grid>

      {/* ----------------------Experiments Panel---------------------------------------- */}
      <Grid item xs={12} lg={6}>
        <Paper elevation={3} sx={{ p: 2, height: "fit-content" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
          >
            <Typography variant="h6">
              {t("overview.MyExperimentsTitle")}
            </Typography>
            <Box display="flex" alignItems="center">
              <Link to="/new-experiment" id="add-experiment-topbar-link">
                <Tooltip title={t("overview.ExperimentButton")}>
                  <IconButton aria-label={t("overview.ExperimentButton")}>
                    <AddCircleIcon />
                  </IconButton>
                </Tooltip>
              </Link>
            </Box>
          </Box>
          {loadingExperiments ? (
            <Box>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                {["Name", "Created", "Processed", "Models"].map(
                  (header, idx) => (
                    <Grid item xs={3} key={idx}>
                      <Skeleton variant="text" height={40} width="80%" />
                    </Grid>
                  )
                )}
              </Grid>
              {/* Rows */}
              {Array.from({ length: 4 }).map((_, rowIndex) => (
                <Grid
                  container
                  spacing={1}
                  key={rowIndex}
                  sx={{ mb: 1 }}
                  alignItems="center"
                >
                  <Grid item xs={3}>
                    <Skeleton variant="text" height={30} />
                  </Grid>
                  <Grid item xs={3}>
                    <Skeleton variant="text" height={30} />
                  </Grid>
                  <Grid item xs={3}>
                    <Skeleton variant="text" height={30} />
                  </Grid>
                  <Grid item xs={3}>
                    <Skeleton variant="text" height={30} />
                  </Grid>
                </Grid>
              ))}
            </Box>
          ) : (
            <DataGrid
              rows={experimentData}
              columns={[
                ...columnInfo,
                {
                  field: "actions",
                  headerName: t("overview.Actions") || "Actions",
                  width: 100,
                  sortable: false,
                  filterable: false,
                  renderCell: (params) =>
                    experimentDeleteLoading === params.row.experimentName ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Tooltip title={t("overview.DeleteExperimentButton")}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteType(
                              "Experiment",
                              null,
                              params.row.experimentName
                            );
                          }}
                          sx={{
                            color: theme.palette.error.main,
                            "&:hover": { bgcolor: theme.palette.error.light },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                },
              ]}
              pageSize={9}
              rowsPerPageOptions={[5]}
              localeText={reactMUILocale}
              getRowId={(row) => experimentData.indexOf(row)}
              components={{ Toolbar: GridToolbar }}
              onSelectionModelChange={(selected) =>
                setSelectedExperiments(selected)
              }
              autoHeight
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: theme.palette.grey[100],
                },
                borderRadius: 1,
              }}
            />
          )}
        </Paper>
      </Grid>

      {/* ------------------------Split Files -------------- */}
      <Grid item xs={12} lg={3}>
        <Paper elevation={3} sx={{ p: 2, height: "fit-content" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <SplitscreenIcon
                sx={{ mr: 1, color: theme.palette.primary.main }}
              />
              {t("overview.SplitFilesTitle")}
            </Typography>
            <Box display="flex" alignItems="center">
              <Chip
                label={splitFiles.length}
                size="small"
                color="primary"
                variant="outlined"
              />
              {datasets.length > 0 && (
                <Link
                  to={`/split-data/${datasets[selectedIndex]?.datasetName}`}
                  style={{ textDecoration: "none" }}
                >
                  <Tooltip title={t("overview.AddSplitButton")}>
                    <IconButton aria-label={t("overview.AddSplitButton")}>
                      <AddCircleIcon />
                    </IconButton>
                  </Tooltip>
                </Link>
              )}
            </Box>
          </Box>

          {loadingSplitFiles ? (
            <Box>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box
                  key={`split-skeleton-${index}`}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Skeleton
                    variant="circular"
                    width={40}
                    height={40}
                    sx={{ mr: 2 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" height={20} width="70%" />
                    <Skeleton variant="text" height={16} width="50%" />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: "auto" }}>
              {splitFiles.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: theme.palette.text.secondary,
                  }}
                >
                  <FolderIcon sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="body2">
                    {t("overview.NoSplitFile")}
                  </Typography>
                  <Typography variant="caption">
                    {t("overview.UploadSplitFileText")}
                  </Typography>
                </Box>
              ) : (
                splitFiles.map((fileName, index) => (
                  <React.Fragment key={`split-file-${index}`}>
                    <ListItem
                      secondaryAction={
                        splitFileDeleteLoading === fileName ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Tooltip title={t("overview.DeleteSplitFileButton")}>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteType("SplitFile", null, fileName);
                              }}
                              sx={{
                                color: theme.palette.error.main,
                                "&:hover": {
                                  bgcolor: theme.palette.error.light,
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )
                      }
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: theme.palette.success.light,
                            width: 32,
                            height: 32,
                          }}
                        >
                          <InsertDriveFileIcon fontSize="small" />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={fileName.slice(0, -4)}
                        primaryTypographyProps={{
                          variant: "body2",
                          noWrap: true,
                        }}
                      />
                    </ListItem>
                    {index < splitFiles.length - 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </React.Fragment>
                ))
              )}
            </List>
          )}
        </Paper>
      </Grid>

      {error && (
        <Grid item xs={12} md={4}>
          <Alert severity="error" sx={{ mt: 2 }} variant="filled">
            {t("experimentErrors.FailedDeletion")}
          </Alert>
        </Grid>
      )}
      {/* ------------------------Dialog Content------------------------------ */}
      <Dialog
        open={open}
        onClose={handleDialogDisplay}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <Alert
            severity="warning"
            className="abortUploadWarning"
            icon={
              <WarningAmberIcon
                sx={{ width: 48, height: 48, color: theme.palette.error.main }}
              />
            }
          >
            <AlertTitle>
              <Typography variant="h5" color="text.secondary">
                {t("common.Warning")}
              </Typography>
            </AlertTitle>

            <Typography variant="body1" color="text.tertiary">
              {(() => {
                if (deleteType === "Dataset")
                  return t("overview.DeleteDatasetWarning");
                if (deleteType === "Experiment")
                  return t("overview.DeleteExperimentWarning");
                if (deleteType === "SplitFile")
                  return t("overview.DeleteSplitFileWarning");
                return "";
              })()}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleButtonClick} autoFocus>
            {t("common.Agree")}
          </Button>
          <Button variant="outlined" onClick={handleDialogDisplay}>
            {t("common.Disagree")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
